import React, { useState, useEffect } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import './Invoice.css';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const AllInvoice = ({ openEditDrawer, refreshTrigger }) => {
    const [invoicesData, setInvoicesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_invoices.php', { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                let fetchedInvoices = res.data.data;
                if (userType === 'administrator') {
                    // Admin sees all
                } else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const invoice of fetchedInvoices) {
                        if (invoice.assigned_users && Array.isArray(invoice.assigned_users)) {
                            const found = invoice.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found && invoice.group_id) { leadersGroupId = invoice.group_id; break; }
                        }
                    }
                    if (leadersGroupId) fetchedInvoices = fetchedInvoices.filter(invoice => invoice.group_id === leadersGroupId);
                    else fetchedInvoices = [];
                } else if (userType === 'member') {
                    fetchedInvoices = fetchedInvoices.filter(invoice => invoice.assigned_users && Array.isArray(invoice.assigned_users) && invoice.assigned_users.some(u => String(u.id) === String(loggedInUserId)));
                }
                setInvoicesData(fetchedInvoices);
            } else setInvoicesData([]);
        } catch (err) { console.error(err); setInvoicesData([]); } finally { setLoading(false); }
    };

    useEffect(() => { fetchInvoices(); }, [refreshTrigger]);

    const handleDelete = async (id) => {
        if (userType !== 'administrator') { alert("Only Super Admin can delete invoices."); return; }
        if (!window.confirm("Are you sure you want to delete this invoice?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/delete_invoice.php', { id }, { headers: { Authorization: `Bearer ${token}` } });
            fetchInvoices();
        } catch (err) { console.error(err); }
    };

    const filteredInvoices = invoicesData.filter(invoice => {
        const term = searchTerm.toLowerCase();
        return (invoice.ref_no || "").toLowerCase().includes(term) || (invoice.client_name || "").toLowerCase().includes(term) || (invoice.status || "").toLowerCase().includes(term);
    });

    // ===== SELECTION =====
    const selection = useTableSelection(filteredInvoices, (row) => row.id);

    // ===== EXPORT CONFIG =====
    const exportColumns = [
        'Invoice #',
        'Client',
        'Invoice Date',
        'Due Date',
        'Chassis',
        'Total Amount',
        'Status',
    ];

    const buildExportRows = (rows) => rows.map(invoice => [
        invoice.ref_no || '',
        invoice.client_name || '',
        invoice.invoice_date || '',
        invoice.due_date || '',
        invoice.chassis || '',
        (Number(invoice.total) || 0).toFixed(2),
        invoice.status || 'Draft',
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Invoices Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

    return (
        <div className="invoice-list-container">
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />

            <div className="table-responsive">
                <table className="invoice-table">
                    <thead>
                        <tr>
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th>Invoice #</th><th>Client</th><th>Invoice Date</th><th>Due Date</th><th>Chassis</th><th>Total Amount</th><th>Status</th><th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={selection.isSelectMode ? 9 : 8} style={{textAlign:"center", padding: 40, color:"#888"}}>Loading Invoices...</td></tr>
                        ) : filteredInvoices.length === 0 ? (
                            <tr><td colSpan={selection.isSelectMode ? 9 : 8} style={{textAlign:"center", padding: 40, color:"#888"}}>
                                {invoicesData.length === 0 ? "No Invoices found." : "No Invoices match the current filter."}
                            </td></tr>
                        ) : (
                            filteredInvoices.map(invoice => (
                                <tr key={invoice.id}>
                                    {selection.isSelectMode && (
                                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(invoice.id)}
                                                onChange={() => selection.toggleRow(invoice.id)}
                                            />
                                        </td>
                                    )}
                                    <td>{invoice.ref_no}</td><td>{invoice.client_name || 'Select Client'}</td><td>{invoice.invoice_date || 'N/A'}</td><td>{invoice.due_date || 'N/A'}</td><td>{invoice.chassis || 'N/A'}</td><td>{(Number(invoice.total) || 0).toFixed(2)}</td>
                                    <td><span className={`status-badge ${invoice.status === 'Paid' ? 'green' : 'orange'}`}>{invoice.status || 'Draft'}</span></td>
                                    <td><div className="action-btns"><button className="action-icon-btn edit" onClick={() => openEditDrawer(invoice.id)}>✎</button><button className="action-icon-btn delete" onClick={() => handleDelete(invoice.id)} disabled={userType !== 'administrator'}>🗑</button></div></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AllInvoice;